import { Stack } from "@mui/material";
import { useFacilityReadOnly } from "hooks/useFacilityReadOnly";
import { Suspense, useState } from "react";

import { ComplianceFindingBadges } from "../../../../Compliance/ComplianceFindingBadge";
import {
  PROFILE_KINDS,
  useEntityComplianceFindings,
} from "../../../../Compliance/useEntityComplianceFindings";
import { WasteProfileSectionsSkeleton } from "../WasteStreamDetailsSkeleton";
import { useWasteStreamProfile } from "./api";
import { CharacterizationSection } from "./CharacterizationSection";
import { ConstituentsSection } from "./ConstituentsSection";
import { EditWasteProfileDialog } from "./EditWasteProfileDialog";
import { ProfileDocumentSection } from "./ProfileDocumentSection";
import { TestingScheduleSection } from "./TestingScheduleSection";
import { TestScheduleDialog } from "./TestScheduleDialog";
import { WasteProfileTestData } from "./types";
import { WasteProfileTestDialog } from "./WasteProfileTestDialog";

const WasteProfileSectionsContent = ({ wasteStreamId }: { wasteStreamId: string }) => {
  const { data } = useWasteStreamProfile(wasteStreamId);
  const wasteStream = data?.wasteStream;
  // This whole block only mounts for container-tracking tenants (the drawer gates
  // it), so the hook needs no separate flag check.
  const { forWasteStream } = useEntityComplianceFindings({
    facilityId: wasteStream?.facilityId,
    kinds: PROFILE_KINDS,
  });
  const { isReadOnly } = useFacilityReadOnly(wasteStream?.facilityId ?? undefined);

  const [editProfileOpen, setEditProfileOpen] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [editingTest, setEditingTest] = useState<WasteProfileTestData | null>(null);

  // The stream is loaded by the drawer around this; a null here means it was
  // deleted between the two queries, and the drawer's own error surface owns
  // that case.
  if (!wasteStream) return null;

  const profile = wasteStream.profile ?? null;

  const openAddTest = () => {
    setEditingTest(null);
    setTestDialogOpen(true);
  };

  const openEditTest = (test: WasteProfileTestData) => {
    setEditingTest(test);
    setTestDialogOpen(true);
  };

  return (
    <>
      <Stack gap={3}>
        <ComplianceFindingBadges findings={forWasteStream(wasteStreamId)} />
        <CharacterizationSection
          wasteStream={wasteStream}
          onEdit={() => setEditProfileOpen(true)}
          isReadOnly={isReadOnly}
        />
        <ProfileDocumentSection wasteStream={wasteStream} isReadOnly={isReadOnly} />
        <ConstituentsSection
          constituents={profile?.constituents ?? []}
          onEdit={() => setEditProfileOpen(true)}
          isReadOnly={isReadOnly}
        />
        <TestingScheduleSection
          profile={profile}
          onAddTest={openAddTest}
          onEditTest={openEditTest}
          onEditSchedule={() => setScheduleOpen(true)}
          isReadOnly={isReadOnly}
        />
      </Stack>

      {editProfileOpen && (
        <EditWasteProfileDialog
          open={editProfileOpen}
          onClose={() => setEditProfileOpen(false)}
          wasteStream={wasteStream}
          isReadOnly={isReadOnly}
        />
      )}
      {testDialogOpen && (
        <WasteProfileTestDialog
          open={testDialogOpen}
          onClose={() => setTestDialogOpen(false)}
          wasteStreamId={wasteStreamId}
          test={editingTest}
          isReadOnly={isReadOnly}
        />
      )}
      {scheduleOpen && (
        <TestScheduleDialog
          open={scheduleOpen}
          onClose={() => setScheduleOpen(false)}
          wasteStreamId={wasteStreamId}
          profile={profile}
          isReadOnly={isReadOnly}
        />
      )}
    </>
  );
};

/**
 * The Project 2 profile surface on the Waste Stream detail drawer. The caller
 * gates this behind `FeatureFlagKey.ContainerTracking`; an un-flagged tenant
 * never mounts it, so the drawer — and its query — are unchanged for them.
 *
 * Its own Suspense boundary keeps the profile query from blocking the rest of
 * the drawer, which already resolved by the time these sections mount.
 */
export const WasteProfileSections = ({ wasteStreamId }: { wasteStreamId: string }) => (
  <Suspense fallback={<WasteProfileSectionsSkeleton />}>
    <WasteProfileSectionsContent wasteStreamId={wasteStreamId} />
  </Suspense>
);
